// ===========================================================================
// NoRecentVisitsTable — tabla paginada (server-side) de pacientes.
//
// Columnas: Member ID (con indicador de carrier ACA/MED), Member Name, DOB,
// Gender, Network, Metal Level (badge), PCP Name (Unassigned si null),
// Specialty, Last Seen, Time Since Last Visit (badge de severidad).
// Click en una fila → abre el drawer de detalle. Orden fijo por nombre (el SP
// no soporta orden dinámico todavía), por eso las columnas no son sortables.
// ===========================================================================
import React, { useMemo } from "react";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { DataGrid } from "@mui/x-data-grid";

import { SeverityBadge, MetalBadge } from "../shared/Badges";
import { formatDate } from "../../utils/format";
import { PAGE_SIZE_OPTIONS, UNASSIGNED_LABEL, EMPTY_PLACEHOLDER } from "../../common/constants";

// Color del puntito de carrier: ACA azul, MED naranja, otro gris.
const carrierColor = (type) => {
  const t = String(type || "").toUpperCase();
  if (t === "ACA") return "#1976d2";
  if (t === "MED" || t === "MEDICARE") return "#ef6c00";
  return "#bdbdbd";
};

const muted = (v) => (v == null || v === "" ? <span style={{ color: "#9ca3af" }}>{EMPTY_PLACEHOLDER}</span> : v);

export default function NoRecentVisitsTable({
  rows,
  rowCount,
  loading,
  page,
  pageSize,
  onPaginationChange,
  onRowClick,
}) {
  const columns = useMemo(
    () => [
      {
        field: "memberId",
        headerName: "Member ID",
        width: 150,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Tooltip title={params.row.memberType || "Unknown carrier"}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: carrierColor(params.row.memberType) }} />
            </Tooltip>
            <Typography variant="body2" noWrap>
              {muted(params.row.memberId)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "memberFullName",
        headerName: "Member Name",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {params.row.memberFullName}
          </Typography>
        ),
      },
      {
        field: "dateOfBirth",
        headerName: "DOB",
        width: 110,
        sortable: false,
        valueGetter: (value) => formatDate(value),
      },
      { field: "gender", headerName: "Gender", width: 80, sortable: false, renderCell: (p) => muted(p.row.gender) },
      {
        field: "networkName",
        headerName: "Network",
        width: 110,
        sortable: false,
        renderCell: (p) => muted(p.row.networkName),
      },
      {
        field: "metalLevel",
        headerName: "Metal",
        width: 100,
        sortable: false,
        renderCell: (p) => <MetalBadge level={p.row.metalLevel} />,
      },
      {
        field: "pcpName",
        headerName: "PCP Name",
        width: 180,
        sortable: false,
        renderCell: (p) =>
          p.row.pcpName ? (
            <Typography variant="body2" noWrap>
              {p.row.pcpName}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "#9ca3af" }}>
              {UNASSIGNED_LABEL}
            </Typography>
          ),
      },
      {
        field: "specialty",
        headerName: "Specialty",
        width: 150,
        sortable: false,
        renderCell: (p) =>
          p.row.specialty ? (
            <Tooltip title={p.row.specialty}>
              <Typography variant="body2" noWrap>
                {p.row.specialty}
              </Typography>
            </Tooltip>
          ) : (
            muted(null)
          ),
      },
      {
        field: "lastSeenDate",
        headerName: "Last Seen",
        width: 110,
        sortable: false,
        valueGetter: (value) => formatDate(value),
      },
      {
        field: "timeSinceLastVisit",
        headerName: "Time Since",
        width: 130,
        sortable: false,
        renderCell: (p) => <SeverityBadge bucket={p.row.timeSinceLastVisit} />,
      },
    ],
    []
  );

  return (
    <Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.cipId}
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={onPaginationChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onRowClick={(params) => onRowClick?.(params.row)}
        disableColumnMenu
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => 56}
        localeText={{ noRowsLabel: "No patients found. Try adjusting the filters." }}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, textTransform: "uppercase", fontSize: "0.72rem" },
        }}
      />
    </Box>
  );
}
